export type HolidayKey =
  | 'tishrei'
  | 'chanukah'
  | 'tu_bishvat'
  | 'purim'
  | 'pesach'
  | 'shavuot'
  | 'summer';

export interface HolidayWindow {
  year: number;
  holiday_key: HolidayKey;
  holiday_label: string;
  holiday_start: string;
  holiday_end: string;
  window_start: string;
  window_end: string;
  lead_days: number;
}

export interface SeasonHit {
  year: number;
  holiday_key: HolidayKey;
  holiday_label: string;
  window_start: string;
  window_end: string;
}

export interface EligibleProduct {
  product_id: number;
  product_code: string;
  name: string;
  production_line_id: number | null;
  production_line_name?: string | null;
}

export interface WeekPeakProduct {
  product_id: number;
  product_code: string;
  name: string;
  cases: number;
}

export interface WeekSeriesPoint {
  week_start: string;
  sales_cases: number;
  production_cases: number;
  seasons: SeasonHit[];
  peak_product?: WeekPeakProduct;
}

export interface ActivitySeason {
  year: number;
  holiday_key: HolidayKey;
  holiday_label: string;
  holiday_start: string;
  holiday_end: string;
  activity_start: string;
  activity_end: string;
}

export interface SeasonAnomalyHit extends ActivitySeason {
  season_cases: number;
  season_week_count: number;
  season_week_avg: number;
  offseason_week_avg: number;
  peak_vs_reg_pct: number;
  why: string;
}

export interface ProductSeasonSeries {
  product_id: number;
  product_code: string;
  name: string;
  production_line_id?: number | null;
  production_line_name?: string | null;
  weeks: WeekSeriesPoint[];
  activity_seasons?: ActivitySeason[];
}

export interface ExtractMeta {
  as_of: string;
  lookback_years: number;
  start_date: string;
  end_date: string;
  eligible_product_count: number;
  sales_week_row_count: number;
  production_week_row_count: number;
}

export interface ForecastStatus {
  extract_ready: boolean;
  graph_ready: boolean;
  extract_meta: ExtractMeta | null;
}

export interface Phase0Row {
  product_id: number;
  product_code: string;
  name: string;
  holiday_key: HolidayKey;
  holiday_label: string;
  year_prior: number | null;
  year_latest: number | null;
  year_prior_start: string | null;
  year_prior_end: string | null;
  year_latest_start: string | null;
  year_latest_end: string | null;
  year_prior_sales: number | null;
  year_latest_sales: number | null;
  year_change_pct: number | null;
  holiday_prior_year: number | null;
  holiday_latest_year: number | null;
  holiday_prior_sales: number | null;
  holiday_latest_sales: number | null;
  holiday_n_pct: number | null;
  next_need_cases: number | null;
  expiration_months: number;
  monthly_sales: number | null;
  expiry_cover_cases: number | null;
  to_make_cases: number | null;
  historical_lead_days: number | null;
  shelf_life_days: number | null;
  earliest_lead_days: number | null;
  remaining_life_days: number;
  next_holiday_start: string | null;
  earliest_produce_date: string | null;
  latest_produce_date: string | null;
  production_line_id: number | null;
  production_line_name: string | null;
  on_hand_cases: number | null;
  why: string;
}

export interface ForecastSeriesResponse {
  extract_meta: ExtractMeta;
  holiday_windows: HolidayWindow[];
  product: ProductSeasonSeries;
  breakdown?: Phase0Row | null;
  other_anomalies?: SeasonAnomalyHit[];
}

export interface SeasonCatalogItem {
  holiday_key: HolidayKey;
  holiday_label: string;
}

export type AnomalyDirection = 'up' | 'down' | 'flat';

export type OrderTrend = 'order_more' | 'order_less' | 'one_time' | 'steady';

export type ProductionAdvice = 'make_more' | 'make_less' | 'watch';

export interface YearToYearChange {
  year: number;
  prior_year: number;
  sales: number;
  prior_sales: number;
  change_pct: number;
  direction: AnomalyDirection;
}

export interface YearSales {
  year: number;
  sales: number;
}

export interface SeasonAnomalyProduct {
  product_id: number;
  product_code: string;
  name: string;
  years_with_season: number[];
  hebrew_years_with_season: number[];
  sales_by_year?: YearSales[];
  year_changes?: YearToYearChange[];
  trend?: OrderTrend;
  latest_year?: number | null;
  latest_sales?: number;
  latest_production?: number;
  advice?: ProductionAdvice;
  client_note?: string;
  breakdown?: Phase0Row | null;
  shifted: boolean;
  sales_cases: number;
  production_cases: number;
  share_pct: number;
  change_pct: number;
  season_week_avg?: number;
  offseason_week_avg?: number;
  peak_vs_reg_pct?: number;
  direction: AnomalyDirection;
}

export interface SeasonViewResponse {
  extract_meta: ExtractMeta;
  available_seasons: SeasonCatalogItem[];
  holiday_key: HolidayKey;
  holiday_label: string;
  lead_days: number;
  trail_days: number;
  years: string[];
  weeks: WeekSeriesPoint[];
  holiday_windows: HolidayWindow[];
  anomaly_products: SeasonAnomalyProduct[];
  top_drivers: SeasonAnomalyProduct[];
}

export const HOLIDAY_LABELS: Record<HolidayKey, string> = {
  tishrei: 'Tishrei',
  chanukah: 'Chanukah',
  tu_bishvat: 'Tu BiShvat',
  purim: 'Purim',
  pesach: 'Pesach',
  shavuot: 'Shavuot',
  summer: 'Summer',
};

export const HOLIDAY_COLORS: Record<HolidayKey, string> = {
  tishrei: '#c2410c',
  chanukah: '#2563eb',
  tu_bishvat: '#65a30d',
  purim: '#db2777',
  pesach: '#dc2626',
  shavuot: '#0891b2',
  summer: '#ca8a04',
};
